/* eslint-disable camelcase */
import { type MouseEvent, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { clsx } from 'clsx'
import { ThumbsDown, ThumbsUp } from 'lucide-react'

import FeedbackFilterBar, { type FeedbackFilters } from './FeedbackFilterBar'
import {
  type DatePreset,
  dayOfSince,
  dayOfUntil,
  matchingPreset,
  presetStart,
  ratesWindow,
  readTimestamp,
} from './feedbackWindow'
import { LIST_PATH, type ListReturn, readListPageState } from './listLocation'
import MetricTiles from './MetricTiles'
import RateTrend, { MAX_DAY_BUCKET_MS, SERIES_COLORS, type TrendSeries } from './RateTrend'
import {
  errorStatus,
  useSummaryFeedbackList,
  useSummaryFeedbackStats,
  useSummaryFeedbackStatsSeries,
  useSummaryQualityStats,
} from './useSummaryFeedback'
import {
  type ComparedVersion,
  labelFor,
  MAX_COMPARED,
  readCompare,
  readModel,
  readPromptVersion,
  writeCompare,
} from './versions'

import styles from './SummaryFeedbackIndexPage.module.scss'

import type { SummaryFeedbackRating } from '@/api/generated/api'
import {
  SUMMARY_FEEDBACK_PAGE_SIZE,
  type SummaryFeedbackListItem,
  type SummaryFeedbackStats,
  type SummaryFeedbackStatsBucket,
} from '@/api/meetingSummaryFeedback'
import Button from '@/components/Button'
import Pagination from '@/components/Pagination'
import { compactAmPm } from '@/utils/compactAmPm'

const EMPTY = '—'

const consentLabels: Record<SummaryFeedbackListItem['consentBasis'], string> = {
  disclosure_v1: 'Accepted disclosure (v1)',
  share_data_election: 'Opted in to data sharing',
  none: 'No consent',
}

const readRating = (value: string | null): SummaryFeedbackRating | undefined =>
  value === 'up' || value === 'down' ? value : undefined

const PRESET_PHRASES: Record<DatePreset, string> = {
  '1d': 'today (UTC)',
  '7d': 'in the last 7 days (UTC)',
  '30d': 'in the last 30 days (UTC)',
}

// Links from before dates lived in the URL carry only `range`; it is converted to `since` once.
const readLegacyRange = (value: string | null): DatePreset | undefined =>
  value === '1d' || value === '7d' || value === '30d' ? value : undefined

const readPage = (value: string | null): number => {
  const page = Number(value)
  return Number.isInteger(page) && page > 1 ? page : 1
}

const When = ({ value }: { value: string }) => {
  const date = new Date(value)
  const short = date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
  return (
    <time dateTime={value} title={date.toLocaleString()}>
      {compactAmPm(short)}
    </time>
  )
}

const Row = ({
  item,
  listReturn,
  onFilterPrompt,
}: {
  item: SummaryFeedbackListItem
  listReturn: ListReturn
  onFilterPrompt: (promptVersion: string) => void
}) => {
  const navigate = useNavigate()
  const prompt = item.summaryPromptVersion
  const RatingIcon = item.rating === 'up' ? ThumbsUp : ThumbsDown
  const detailPath = `${LIST_PATH}/${encodeURIComponent(item.id)}`
  // A stretched ::after link can't be scoped to a <tr> (Safari ignores position on table rows), so the row clicks itself.
  const openRow = (event: MouseEvent<HTMLTableRowElement>) => {
    if ((event.target as HTMLElement).closest('a, button') || window.getSelection()?.toString()) return
    if (event.metaKey || event.ctrlKey) {
      window.open(detailPath, '_blank', 'noopener')
      return
    }
    navigate(detailPath, { state: listReturn })
  }
  return (
    <tr className={clsx(styles.row, item.clearedAt && styles.withdrawnRow)} onClick={openRow}>
      <td className={styles.when}>
        <Link to={detailPath} state={listReturn} className={styles.rowLink}>
          <When value={item.createdAt} />
        </Link>
      </td>
      <td>
        <span className={styles.rating}>
          <RatingIcon size={14} aria-hidden="true" />
          {item.rating === 'up' ? 'Up' : 'Down'}
        </span>
      </td>
      <td className={styles.reporter}>
        {item.reporterEmail ?? 'Unknown'}{' '}
        <span className={clsx(styles.tag, item.reporterIsInternal && styles.tagInternal)}>
          {item.reporterIsInternal ? 'Internal' : 'External'}
        </span>
      </td>
      <td className={styles.note}>{item.feedbackPreview ?? EMPTY}</td>
      <td>{consentLabels[item.consentBasis]}</td>
      <td className={styles.prompt}>
        {item.promptLabel && <span>{item.promptLabel}</span>}
        {prompt ? (
          <button
            type="button"
            className={styles.promptButton}
            aria-label={`Filter by prompt ${prompt}`}
            onClick={(event) => {
              event.stopPropagation()
              onFilterPrompt(prompt)
            }}
          >
            <code>{prompt}</code>
          </button>
        ) : (
          !item.promptLabel && EMPTY
        )}
      </td>
      <td className={styles.model}>{item.summaryModel ?? EMPTY}</td>
      <td>
        <span className={styles.badges}>
          {item.isSuperseded && (
            <span className={styles.badge} title="This user rated the meeting again later">
              Superseded
            </span>
          )}
          {item.clearedAt && <span className={styles.badge}>Withdrawn</span>}
          {item.meetingDeleted && <span className={clsx(styles.badge, styles.badgeDeleted)}>Meeting deleted</span>}
          {item.hasSummarySnapshot && <span className={clsx(styles.badge, styles.badgeSnapshot)}>Snapshot</span>}
        </span>
      </td>
    </tr>
  )
}

const datePhrase = ({ since, until }: FeedbackFilters) => {
  const preset = matchingPreset(since, until)
  if (preset) return PRESET_PHRASES[preset]
  if (since && until) return `from ${dayOfSince(since)} to ${dayOfUntil(until)} (UTC)`
  if (since) return `since ${dayOfSince(since)} (UTC)`
  if (until) return `through ${dayOfUntil(until)} (UTC)`
  return undefined
}

const ratingKind = ({ rating }: FeedbackFilters) => {
  if (rating === 'up') return 'thumbs-up'
  if (rating === 'down') return 'thumbs-down'
  return undefined
}

const scopePhrase = (filters: FeedbackFilters) =>
  [
    filters.promptVersion && `for prompt ${filters.promptVersion}`,
    filters.summaryModel && `with model ${filters.summaryModel}`,
    datePhrase(filters),
  ]
    .filter(Boolean)
    .join(' ')

const emptyMessage = (filters: FeedbackFilters) => {
  const kind = ratingKind(filters) ?? 'summary'
  const scope = scopePhrase(filters)
  return scope ? `No ${kind} feedback ${scope}.` : `No ${kind} feedback yet.`
}

const totalMessage = (total: number, filters: FeedbackFilters) =>
  [total.toLocaleString(), ratingKind(filters), total === 1 ? 'rating' : 'ratings', scopePhrase(filters)]
    .filter(Boolean)
    .join(' ')

const SummaryFeedbackIndexPage = () => {
  const location = useLocation()
  const [search, setSearch] = useSearchParams()
  const pageRef = useRef<HTMLElement>(null)
  const tableRef = useRef<HTMLDivElement>(null)
  const rating = readRating(search.get('rating'))
  const promptVersion = readPromptVersion(search.get('prompt'))
  const summaryModel = readModel(search.get('model'))
  const legacyRange = readLegacyRange(search.get('range'))
  const urlSince = readTimestamp(search.get('since'))
  const until = readTimestamp(search.get('until'))
  const cursor = search.get('cursor') || undefined
  const page = cursor ? readPage(search.get('page')) : 1
  const earlierCursors = readListPageState(location.state)?.cursors ?? []
  const compare = readCompare(search.get('compare'))

  const legacySince = useMemo(
    () => (legacyRange && !urlSince ? presetStart(legacyRange) : undefined),
    [legacyRange, urlSince]
  )
  const since = urlSince ?? legacySince
  useEffect(() => {
    if (!legacyRange) return
    setSearch(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (legacySince) next.set('since', legacySince)
        next.delete('range')
        return next
      },
      { replace: true, state: location.state }
    )
  }, [legacyRange, legacySince, setSearch, location.state])

  const filters: FeedbackFilters = { rating, promptVersion, summaryModel, since, until }
  const query = useSummaryFeedbackList(filters, cursor)
  const status = errorStatus(query.error)
  const isDenied = status === 401 || status === 403

  const rateWindow = ratesWindow(since, until)
  const isDayAllowed = Date.parse(rateWindow.until) - Date.parse(rateWindow.since) <= MAX_DAY_BUCKET_MS
  const bucket: SummaryFeedbackStatsBucket = search.get('bucket') === 'week' || !isDayAllowed ? 'week' : 'day'
  const statsParams = { ...rateWindow, bucket }
  const stats = useSummaryFeedbackStats({ ...statsParams, promptVersion, summaryModel }, !isDenied)
  // The tiles read only overall values, so a fixed bucket keeps the day/week toggle from refetching them.
  const quality = useSummaryQualityStats({ ...rateWindow, bucket: 'week', promptVersion, summaryModel }, !isDenied)
  const compared = useSummaryFeedbackStatsSeries(
    compare.map((version) => ({ ...statsParams, promptVersion: version.promptVersion, summaryModel: version.model })),
    !isDenied
  )
  const failedCompared = compared.filter((result) => !result.data && result.isError)
  // byVersion ignores the prompt and model filters, so the version list survives a filter change while counts reload.
  const versions = stats.data?.byVersion ?? []
  const currentStats = stats.isPlaceholderData ? undefined : stats.data
  // The tiles ignore the bucket, so switching day/week keeps them up while the chart reloads.
  const tilesKey = [rateWindow.since, rateWindow.until, promptVersion, summaryModel].join('|')
  const [settledCounts, setSettledCounts] = useState<{ key: string; overall: SummaryFeedbackStats['overall'] }>()
  if (currentStats && (settledCounts?.key !== tilesKey || settledCounts.overall !== currentStats.overall))
    setSettledCounts({ key: tilesKey, overall: currentStats.overall })
  const counts = settledCounts?.key === tilesKey ? settledCounts.overall : undefined
  const currentQuality = quality.isPlaceholderData ? undefined : quality.data
  const isQualityFailed = !currentQuality && quality.isError && errorStatus(quality.error) !== 403
  const series: TrendSeries[] = compare.length
    ? compare.map((version, index) => ({
        key: `version${index}`,
        label: labelFor(version, versions),
        color: SERIES_COLORS[index],
        stats: compared[index]?.isPlaceholderData ? undefined : compared[index]?.data,
      }))
    : [{ key: 'current', label: 'Current filters', color: SERIES_COLORS[0], stats: currentStats }]

  const items = query.data?.items
  const nextCursor = query.data?.nextCursor ?? null
  const listReturn: ListReturn = { search: location.search, listState: { cursors: earlierCursors } }

  const setFilters = (next: FeedbackFilters) => {
    const params = new URLSearchParams(search)
    const values = {
      rating: next.rating,
      prompt: next.promptVersion,
      model: next.summaryModel,
      range: undefined,
      view: undefined,
      since: next.since,
      until: next.until,
      cursor: undefined,
      page: undefined,
    }
    for (const [key, value] of Object.entries(values)) {
      if (value) params.set(key, value)
      else params.delete(key)
    }
    setSearch(params)
  }
  // The chart controls leave the table alone, so they keep its page and the cursors Back relies on.
  const setChartParam = (key: string, value: string | undefined) => {
    const params = new URLSearchParams(search)
    if (value) params.set(key, value)
    else params.delete(key)
    setSearch(params, { state: location.state })
  }
  const selectCompare = (next: ComparedVersion[]) => setChartParam('compare', writeCompare(next.slice(0, MAX_COMPARED)))

  const goToPage = (nextPage: number, nextPageCursor: string | undefined, cursors: string[]) => {
    const params = new URLSearchParams(search)
    if (nextPageCursor) {
      params.set('cursor', nextPageCursor)
      params.set('page', String(nextPage))
    } else {
      params.delete('cursor')
      params.delete('page')
    }
    setSearch(params, { state: { cursors } })
    pageRef.current?.scrollIntoView?.({ block: 'start' })
    tableRef.current?.focus({ preventScroll: true })
  }
  const goNext = () => {
    if (nextCursor && !query.isPlaceholderData) goToPage(page + 1, nextCursor, [...earlierCursors, cursor ?? ''])
  }
  // Opened from a link with no history of earlier pages, Previous falls back to the first page.
  const goPrev = () => {
    const previous = earlierCursors.at(-1)
    if (previous === undefined) goToPage(1, undefined, [])
    else goToPage(page - 1, previous || undefined, earlierCursors.slice(0, -1))
  }

  const tiles = !isDenied && (
    <>
      <MetricTiles
        counts={counts}
        quality={currentQuality}
        isCountsLoading={!counts && stats.isFetching}
        isQualityLoading={!currentQuality && quality.isFetching}
      />
      {isQualityFailed && (
        <div className={styles.statsError} role="alert">
          <span>Couldn&apos;t load summary runs, latency and cost.</span>
          <Button title="Retry" type="light" isLoading={quality.isFetching} onClick={() => void quality.refetch()} />
        </div>
      )}
    </>
  )

  let rates
  if (isDenied) rates = null
  else if (!stats.data && stats.isError) {
    rates = (
      <div className={styles.statsError} role="alert">
        <span>Couldn&apos;t load rates. The ratings below are unaffected.</span>
        <Button title="Retry rates" type="light" isLoading={stats.isFetching} onClick={() => void stats.refetch()} />
      </div>
    )
  } else {
    rates = (
      <>
        <p className={styles.tilesCaption}>
          Each user&apos;s latest rating per meeting · withdrawn ratings left out
          {rating && ' · 👍/👎 filter applies to the table only'}
        </p>
        {failedCompared.length ? (
          <div className={styles.statsError} role="alert">
            Couldn&apos;t load the compared versions.
            <Button
              title="Retry"
              type="light"
              isLoading={failedCompared.some((result) => result.isFetching)}
              onClick={() => failedCompared.forEach((result) => void result.refetch())}
            />
            <Button title="Stop comparing" type="light" onClick={() => selectCompare([])} />
          </div>
        ) : (
          <RateTrend
            {...rateWindow}
            bucket={bucket}
            isDayAllowed={isDayAllowed}
            onBucket={(value) => setChartParam('bucket', value === 'day' ? undefined : value)}
            series={series}
            versions={versions}
            compare={compare}
            onCompare={selectCompare}
          />
        )}
      </>
    )
  }

  let body
  if (items && items.length === 0 && cursor) {
    body = (
      <div className={styles.state}>
        <span>No more feedback on this page.</span>
        <Button title="Go to the first page" type="light" onClick={() => goToPage(1, undefined, [])} />
      </div>
    )
  } else if (items && items.length === 0) {
    body = <div className={styles.state}>{emptyMessage(filters)}</div>
  } else if (items) {
    body = (
      <>
        <p className={styles.totalLine} aria-live="polite">
          {totalMessage(query.data?.total ?? items.length, filters)}
        </p>
        <div
          ref={tableRef}
          className={styles.tableScroll}
          tabIndex={-1}
          aria-label="Summary feedback ratings"
          aria-busy={query.isPlaceholderData}
        >
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">Rating</th>
                <th scope="col">Reporter</th>
                <th scope="col">Note</th>
                <th scope="col">Consent</th>
                <th scope="col">Prompt</th>
                <th scope="col">Model</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <Row
                  key={item.id}
                  item={item}
                  listReturn={listReturn}
                  onFilterPrompt={(value) => setFilters({ ...filters, promptVersion: value })}
                />
              ))}
            </tbody>
          </table>
        </div>
        {(page > 1 || nextCursor) && (
          <div className={styles.pager}>
            <Pagination
              page={page - 1}
              pageSize={SUMMARY_FEEDBACK_PAGE_SIZE}
              total={query.data?.total ?? 0}
              onPrev={goPrev}
              onNext={goNext}
            />
          </div>
        )}
      </>
    )
  } else if (isDenied) {
    body = (
      <div className={styles.state} role="alert">
        You need internal admin access to view summary feedback.
      </div>
    )
  } else if (query.isError) {
    body = (
      <div className={styles.state} role="alert">
        <span>Couldn&apos;t load summary feedback.</span>
        <Button title="Retry" type="light" isLoading={query.isFetching} onClick={() => void query.refetch()} />
        {cursor && <Button title="Go to the first page" type="light" onClick={() => goToPage(1, undefined, [])} />}
      </div>
    )
  } else {
    body = (
      <div className={styles.state} role="status">
        Loading summary feedback…
      </div>
    )
  }

  return (
    <main ref={pageRef} className={styles.page}>
      <Helmet>
        <title>Summary feedback</title>
      </Helmet>
      <div className={styles.content}>
        <header className={styles.header}>
          <h1>Summary feedback</h1>
          <p className={styles.subtitle}>Ratings users left on meeting summaries. Every view is audited.</p>
        </header>
        <FeedbackFilterBar values={filters} versions={versions} onChange={setFilters} />
        {tiles}
        {rates}
        <section aria-label="Ratings">{body}</section>
      </div>
    </main>
  )
}

export default SummaryFeedbackIndexPage
